import type { MedplumClient } from '@medplum/core';
import { type FC, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Text,
} from '@eluve/components';

import { ExtractedObservations } from './ExtractedObservations';

/**
 * Renders a PDF fitted to the pane width with react-pdf (pdfjs) — full control,
 * no browser PDF toolbar/sidebar, and consistent across browsers. The pdfjs
 * worker is configured app-wide in web-vite's `main.tsx`. Pages render as
 * canvases (text/annotation layers off) since this is a read-only reference.
 */
const PdfPane: FC<{ url: string }> = ({ url }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full overflow-y-auto bg-muted/30 p-3">
      <Document
        file={url}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        loading={
          <Text color="tertiary" className="p-4">
            Loading document…
          </Text>
        }
        error={
          <Text color="tertiary" className="p-4">
            Couldn&apos;t display this document.
          </Text>
        }
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Page
            key={i}
            pageNumber={i + 1}
            width={width > 24 ? width - 24 : undefined}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="mb-3 overflow-hidden rounded-md border border-border-secondary shadow-sm last:mb-0"
          />
        ))}
      </Document>
    </div>
  );
};

interface SourceDoc {
  url: string;
  name: string;
  contentType: string;
}

/** Derive a `Binary/<id>` reference from an attachment URL. A fresh
 *  DocumentReference read can hand back a bare `/storage/<id>/…` path with no
 *  presigned signature (this self-hosted Medplum), which 401s in an iframe — so
 *  download the Binary through the authenticated FHIR endpoint instead. */
const binaryRef = (attachmentUrl: string): string => {
  const storageId = attachmentUrl.match(/\/storage\/([^/?]+)/)?.[1];
  return attachmentUrl.startsWith('Binary/')
    ? attachmentUrl
    : storageId
      ? `Binary/${storageId}`
      : attachmentUrl;
};

/**
 * Near-fullscreen review surface: the source document on the left and its
 * extracted values (review / edit / approve) on the right, so the reviewer can
 * cross-reference the original while reviewing. Opened from the Patient Uploads
 * "View Details" action and from the Observations list's "Review" action.
 */
export const ReviewDocumentModal: FC<{
  client: MedplumClient;
  fhirPatientId: string;
  /** Source DocumentReference id to review; null = closed. */
  documentReferenceId: string | null;
  onOpenChange: (open: boolean) => void;
}> = ({ client, fhirPatientId, documentReferenceId, onOpenChange }) => {
  const [source, setSource] = useState<SourceDoc | null>(null);

  useEffect(() => {
    if (!documentReferenceId) {
      setSource(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const doc = await client.readResource(
          'DocumentReference',
          documentReferenceId,
        );
        const attachment = doc.content?.[0]?.attachment;
        if (attachment?.url) {
          const blob = await client.download(binaryRef(attachment.url));
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setSource({
            url: objectUrl,
            name: attachment.title ?? doc.description ?? 'Document',
            contentType: attachment.contentType ?? 'application/pdf',
          });
        }
      } catch {
        // Left pane shows a graceful fallback; the values panel still works.
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, documentReferenceId]);

  return (
    <Dialog open={documentReferenceId !== null} onOpenChange={onOpenChange}>
      <DialogContent
        size="full"
        className="flex h-[88vh] max-h-[88vh] flex-col overflow-hidden p-0"
      >
        <DialogHeader className="shrink-0 border-b px-4 py-3">
          <DialogTitle>Review extracted results</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          {/* Left: the source document. */}
          <div className="hidden min-h-0 w-1/2 shrink-0 border-r bg-muted/20 md:block">
            {source ? (
              source.contentType.startsWith('image/') ? (
                <div className="h-full overflow-y-auto bg-muted/30 p-3">
                  <img
                    src={source.url}
                    alt={source.name}
                    className="mx-auto w-full max-w-full"
                  />
                </div>
              ) : (
                <PdfPane url={source.url} />
              )
            ) : (
              <div className="flex h-full items-center justify-center p-4">
                <Text color="tertiary">Loading document…</Text>
              </div>
            )}
          </div>
          {/* Right: the extracted values (review / edit / approve). */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {documentReferenceId && (
              <ExtractedObservations
                client={client}
                fhirPatientId={fhirPatientId}
                documentReferenceId={documentReferenceId}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
